<template>
  <!-- XOL-602/603/604 (UX spec sections 4 and 5, R2.3.2): the page a password link opens.
       Routes /reset-password/:token (a reset) and /set-password/:token (an invitation to set
       a first password), rendered by Login.vue in mode `link`. Signed-out browsers only: the
       guard (lib/authGuard.js) sends a signed-in browser to the app and the page never
       renders (D11), so there is no signed-in variant anywhere in this file.

       THE TOKEN. It arrives as the `token` prop (the route param) and is sent in a POST BODY
       field named `token`, never in a URL, Vuex or storage: the API logs every request URL
       and redacts only the body keys `token` and `password`. -->
  <login-panel>

    <!-- Checking the link (while POST /password/token is in flight). -->
    <template v-if="state === 'checking'">
      <p role="status" class="login-panel__loading"><i class="fa fa-spinner fa-spin text-primary" aria-hidden="true"></i> Checking your link…</p>
    </template>

    <!-- The inspect call failed for a reason that says nothing about the link. -->
    <template v-else-if="state === 'inspect_error'">
      <el-alert v-if="inspectRate" type="warning" show-icon :closable="false"
        title="Too many attempts." description="Wait one minute, then try again." />
      <el-alert v-else type="error" show-icon :closable="false" title="Couldn't check this link." />
      <div class="login-panel__actions">
        <div><el-button size="small" @click="inspect">Try again</el-button></div>
        <div><el-button type="text" icon="fa fa-chevron-left" @click="goLogin">Back to login</el-button></div>
      </div>
    </template>

    <!-- 5. Link states: a 410 (or a 422 on inspect). One alert, then the next step. -->
    <template v-else-if="state === 'problem'">
      <el-alert :type="problem.type" show-icon :closable="false" :title="problem.title" :description="problem.desc" />
      <div class="login-panel__actions">
        <div v-for="(b, i) in problem.buttons" :key="b.key">
          <el-button v-if="i === 0" ref="firstAction" type="primary" :icon="b.icon" @click="act(b.key)">{{ b.label }}</el-button>
          <el-button v-else type="text" :icon="b.icon" @click="act(b.key)">{{ b.label }}</el-button>
        </div>
      </div>
    </template>

    <!-- 4. The form. -->
    <template v-else>
      <h4 ref="heading" class="login-panel__title" tabindex="-1">{{ isInvite ? 'Set your password' : 'Choose a new password' }}</h4>
      <p class="login-panel__text">{{ intro }}</p>

      <!-- Lets a password manager save the new password under the right account. -->
      <input type="text" class="login-panel__sr" autocomplete="username" :value="login" readonly tabindex="-1" aria-hidden="true">

      <el-form label-position="top" @submit.native.prevent>
        <el-form-item label="Login ID"><b>{{ login }}</b></el-form-item>
      </el-form>

      <div @keyup.enter="onEnter">
        <password-form v-model="form" :inline="false" :password-error="serverError"
          @confirm-blur="confirmTouched = true" />
      </div>

      <small v-if="expiresText" class="login-panel__meta">This link expires on {{ expiresText }}.</small>

      <el-alert v-if="alertKey" id="password-rules" type="error" show-icon :closable="false" :title="alertText" />
      <el-alert v-if="alert === 'rate'" type="warning" show-icon :closable="false"
        title="Too many attempts." description="Wait one minute, then try again." />
      <el-alert v-if="alert === 'error'" type="error" show-icon :closable="false"
        title="Couldn't save your password. Try again." />

      <div class="login-panel__actions">
        <div>
          <el-button type="success" icon="fa fa-check" :loading="saving"
            :disabled="saving || !!problemKey" @click="submit">{{ saving ? 'Saving…' : 'Save password' }}</el-button>
        </div>
        <div><el-button type="text" icon="fa fa-chevron-left" @click="goLogin">Back to login</el-button></div>
      </div>
    </template>

  </login-panel>
</template>

<script>

import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import LoginPanel from '@/components/login/LoginPanel'
import PasswordForm from '@/components/login/PasswordForm'
import { passwordProblem, passwordProblemText, visiblePasswordProblem } from '@/lib/passwordPolicy'
import { errStatus, linkCode, fieldMessage, wasRetried } from '@/lib/linkErrors'

export default {

  name: 'reset-password',

  mixins: [DateMixins],

  components: {
    LoginPanel,
    PasswordForm
  },

  props: {
    // The link's secret, from the route. Never copied anywhere but the request body.
    token: {
      type: String,
      required: true
    },
    // 'reset' (/reset-password) or 'invite' (/set-password): from the route (UX section 4).
    purpose: {
      type: String,
      default: 'reset'
    }
  },

  data () {
    return {
      // 'checking' | 'form' | 'problem' | 'inspect_error'
      state: 'checking',
      // problem: 'expired' | 'used' | 'used_retry' | 'invalid' | 'account_unavailable'
      problemCode: 'invalid',
      inspectRate: false,

      // From the inspect response.
      login: '',
      name: '',
      expiresAt: null,

      form: { password: null, c_password: null },
      confirmTouched: false,
      saving: false,
      // The API's first 422 message for the password.
      serverError: '',
      // null | 'rate' | 'error'
      alert: null,
      // A submit ended without a definite answer (a 5xx, a timeout): the password may
      // have been saved. A later 410 `used` must then say so.
      submitUncertain: false
    }
  },

  computed: {
    isInvite () {
      return this.purpose === 'invite'
    },

    intro () {
      var hello = this.name ? 'Hello ' + this.name + '.' : 'Hello.'
      return hello + (this.isInvite
        ? ' Your account is ready. Choose a password to start using it.'
        : ' Choose a new password for your account.')
    },

    expiresText () {
      return this.expiresAt ? this.formatDateTime(this.expiresAt) : ''
    },

    // null when acceptable, else the first problem (too_short included: it only disables Save).
    problemKey () {
      return passwordProblem(this.form.password, this.form.c_password, this.login)
    },

    // The problem to show: only once the confirm field has been left, never too_short.
    alertKey () {
      return visiblePasswordProblem(this.form.password, this.form.c_password, this.login, this.confirmTouched)
    },

    alertText () {
      return passwordProblemText(this.alertKey)
    },

    // The 410 states of UX section 5.
    problem () {
      var inv = this.isInvite
      var c = this.problemCode
      var type, title, desc, order

      if (c === 'expired') {
        type = 'warning'
        title = inv ? 'This invitation has expired' : 'This link has expired'
        desc = inv ? 'Invitation links expire after 3 days. Ask your administrator to send you a new one.' : 'Reset links expire after 60 minutes. Ask for a new one.'
        order = inv ? ['back'] : ['send', 'back']
      } else if (c === 'used') {
        type = 'info'
        title = inv ? 'This invitation has already been used' : 'This link has already been used'
        desc = inv ? 'If you have already set your password, log in with it. If not, ask your administrator for a new invitation.' : 'If you have just set your password, log in with it. If not, ask for a new link.'
        order = inv ? ['back'] : ['back', 'send']
      } else if (c === 'used_retry') {
        type = 'warning'
        title = 'Your password may already be saved'
        desc = 'We could not confirm it, but this link has now been used. Try to log in with your new password. If that does not work, ask for a new link.'
        order = inv ? ['back'] : ['back', 'send']
      } else if (c === 'account_unavailable') {
        type = 'error'
        title = "This account can't be used"
        desc = 'Please contact your administrator.'
        order = ['back']
      } else {
        type = 'error'
        title = inv ? 'This invitation link is not valid' : 'This link is not valid'
        desc = inv ? 'The link may be cut off. Open the newest e-mail, or ask your administrator for a new invitation.' : 'The link may be cut off. Open the newest e-mail and click its button, or ask for a new link.'
        order = inv ? ['back'] : ['send', 'back']
      }

      var buttons = order.map(function (key, i) {
        if (key === 'send') {
          return { key: 'send', label: 'Send a new link', icon: 'fa fa-paper-plane' }
        }
        return { key: 'back', label: 'Back to login', icon: i === 0 ? 'fa fa-right-to-bracket' : 'fa fa-chevron-left' }
      })

      return { type: type, title: title, desc: desc, buttons: buttons }
    }
  },

  watch: {
    // A server 422 belongs to the value that was sent: editing either field clears it.
    'form.password': function () { this.serverError = '' },
    'form.c_password': function () { this.serverError = '' }
  },

  methods: {

    // POST /password/token {token}: shows what the link is for. Never uses it up.
    inspect () {
      this.state = 'checking'
      this.inspectRate = false

      axios.post(this.getEndpoint('password/token', null, false), { token: this.token })
        .then(response => {
          var data = (response && response.data) || {}
          this.login = data.login || ''
          this.name = data.name || ''
          this.expiresAt = data.expires_at || null
          this.state = 'form'
          this.focusHeading()
        }, err => {
          var status = errStatus(err)

          // A 410 says something about the LINK, not the session: nothing here touches auth.
          if (status === 410) {
            this.showProblem(linkCode(err))
          } else if (status === 422) {
            // A malformed token is "not valid", like an unknown one.
            this.showProblem('invalid')
          } else {
            this.inspectRate = status === 429
            this.state = 'inspect_error'
          }
        })
    },

    // POST /password/reset {token, password}: sets the password and uses the link up.
    submit () {
      if (this.saving || this.problemKey) {
        return
      }

      this.saving = true
      this.alert = null
      this.serverError = ''

      axios.post(this.getEndpoint('password/reset', null, false), {
        token: this.token,
        password: this.form.password.trim()
      })
        .then(() => {
          this.saving = false
          this.form.password = null
          this.form.c_password = null
          // Success lives in memory on the Login screen, and the token URL leaves the
          // current history entry.
          this.$emit('done', { variant: this.isInvite ? 'invite' : 'reset', login: this.login })
          this.$router.replace('/password-set')
        }, err => {
          this.saving = false
          var status = errStatus(err)

          if (status === 422) {
            var message = fieldMessage(err, 'password')
            if (message) {
              // The token is NOT used up by a 422: correct it and save again.
              this.serverError = message
              this.focusPassword()
            } else {
              this.showProblem('invalid')
            }
          } else if (status === 410) {
            var code = linkCode(err)
            // `used` right after a submit that may have succeeded (timed out, or replayed by
            // axios-retry): this link was probably used by THIS save.
            if (code === 'used' && (this.submitUncertain || wasRetried(err))) {
              code = 'used_retry'
            }
            this.showProblem(code)
          } else if (status === 429) {
            this.alert = 'rate'
          } else {
            this.submitUncertain = true
            this.alert = 'error'
          }
        })
    },

    showProblem (code) {
      this.problemCode = code
      this.state = 'problem'
      // The XOL-647 terminal-state rule: focus goes to the first button.
      this.$nextTick(() => {
        var first = this.$refs.firstAction
        first = Array.isArray(first) ? first[0] : first
        if (first && first.$el && typeof first.$el.focus === 'function') {
          first.$el.focus()
        }
      })
    },

    // Enter in a password field submits when there is nothing to fix. Not the reveal toggle:
    // Enter there toggles it, and must not also save.
    onEnter (event) {
      if (event && event.target && event.target.tagName === 'INPUT') {
        this.submit()
      }
    },

    act (key) {
      this.$router.push(key === 'send' ? '/forgot-password' : '/login')
    },

    goLogin () {
      this.$router.push('/login')
    },

    focusHeading () {
      this.$nextTick(() => {
        var heading = this.$refs.heading
        if (heading && typeof heading.focus === 'function') {
          heading.focus()
        }
      })
    },

    focusPassword () {
      this.$nextTick(() => {
        var field = this.$el && this.$el.querySelector ? this.$el.querySelector('.Password__field') : null
        if (field) {
          field.focus()
        }
      })
    }

  },

  created () {
    this.inspect()
  }

}

</script>
